import type { ChangeEvent } from 'react';
import { CALENDAR } from '@locales/id/messages';
import { dayParts, daysInMonth, joinDayParts, type DayParts } from '@lib/utils/calendar';

const pad = (n: number) => String(n).padStart(2, '0');

/** The years a to b, both included (none when b < a); `reverse` for the newest first. */
export const yearRange = (a: number, b: number, reverse = false) => {
  const ys = Array.from({ length: Math.max(0, b - a + 1) }, (_, i) => a + i);
  return reverse ? ys.reverse() : ys;
};

/**
 * One calendar date as three Indonesian selects, tanggal · bulan · tahun ("5" "Maret" "2031"), never the browser's
 * date input (it speaks the device's language and is hard to use on many phones). Native selects: every keyboard,
 * screen reader and phone knows them; the group carries the field's label, each select its part. The value is
 * "YYYY-MM-DD" once whole, the parts chosen so far otherwise (lib/utils/calendar › dayParts): the caller validates,
 * this only never offers a day the month does not have (a 31 becomes the month's last day when the month changes).
 * A stored year outside `years` stays selectable.
 */
export function DateField({
  id,
  label,
  describedBy,
  value,
  onChange,
  years,
  invalid,
}: {
  id: string;
  label: string;
  describedBy?: string;
  value: string;
  onChange: (v: string) => void;
  years: readonly number[];
  invalid?: boolean;
}) {
  const p = dayParts(value);
  const last = p.y && p.m ? daysInMonth(Number(p.y), Number(p.m)) : 31;
  const shownYears = p.y && !years.includes(Number(p.y)) ? [Number(p.y), ...years] : years;
  const set = (patch: Partial<DayParts>) => {
    const next = { ...p, ...patch };
    // a day the new month or year does not have becomes its last day
    if (next.d && next.y && next.m) next.d = pad(Math.min(Number(next.d), daysInMonth(Number(next.y), Number(next.m))));
    onChange(joinDayParts(next));
  };
  const common = { 'aria-describedby': describedBy, 'aria-invalid': invalid || undefined, className: 'input-lg' };
  const on = (k: keyof DayParts) => (e: ChangeEvent<HTMLSelectElement>) => set({ [k]: e.target.value });
  return (
    <div className="datefield" role="group" aria-label={label}>
      <select id={id} aria-label={CALENDAR.day} value={p.d} onChange={on('d')} {...common}>
        <option value="">{CALENDAR.dayShort}</option>
        {Array.from({ length: last }, (_, i) => (
          <option key={i} value={pad(i + 1)}>
            {i + 1}
          </option>
        ))}
      </select>
      <select aria-label={CALENDAR.month} value={p.m} onChange={on('m')} {...common}>
        <option value="">{CALENDAR.month}</option>
        {CALENDAR.months.map((n, i) => (
          <option key={n} value={pad(i + 1)}>
            {n}
          </option>
        ))}
      </select>
      <select aria-label={CALENDAR.year} value={p.y} onChange={on('y')} {...common}>
        <option value="">{CALENDAR.year}</option>
        {shownYears.map((y) => (
          <option key={y} value={String(y)}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );
}
